/**
 * S2-P4-LEAKAGE-DASHBOARD-FOUNDATION-01 - pure presentation/view-model for the
 * booking-cohort appointment funnel block on the Owner Leakage Snapshot.
 *
 * WHY THIS EXISTS
 *   The backend already produces a PHI-free, booking-cohort
 *   `OwnerLeakageAppointmentFunnel` (see api/routes/owner.ts). This module is
 *   the presentation layer: it maps that block into stable, display-ready rows
 *   (ordered buckets, the 8 reason tokens with labels, per-clinic / per-source
 *   rows, and formatted stage rates) so the panel stays declarative.
 *
 * SAFETY BY CONSTRUCTION
 *   - PURE + DETERMINISTIC: same input -> same output. No I/O, no fetch, no env,
 *     no Date.now(), no Math.random(), no React, no Supabase, no API import.
 *   - PHI-FREE: the input carries only counts, clinic ids, source tokens, and
 *     the 8 controlled not-converted reason tokens. This module adds no patient
 *     identifier and never invents data.
 *   - HONEST RATES: a null stage rate (booked = 0) renders as 'n/a', never as
 *     a fabricated 0%.
 */

import { statusChipLabel } from '@/dashboard/ui/status-canon';
import type {
  OwnerLeakageAppointmentFunnel,
  OwnerLeakageFunnelCounts,
  OwnerLeakageFunnelPerClinic,
  OwnerLeakageFunnelPerSource,
  OwnerLeakageNotConvertedReason,
} from '@/dashboard/types';

/** Display order for the 5 cohort buckets (the cohort denominator aside). */
export const FUNNEL_BUCKET_ORDER = [
  'active',
  'fulfilled',
  'notConverted',
  'cancelled',
  'noShow',
] as const;

export type FunnelBucketKey = (typeof FUNNEL_BUCKET_ORDER)[number];

// RECEPTION-OS-BUILD-V1 (F-09): the bucket that borrows the appointment-status
// word resolves through THE ONE StatusChip canon (mechanical import update);
// the funnel-only bucket concepts (Still active / Not converted) stay local.
const BUCKET_LABELS: Record<FunnelBucketKey, string> = {
  active: 'Still active',
  fulfilled: statusChipLabel('appointment', 'fulfilled'),
  notConverted: 'Not converted',
  cancelled: statusChipLabel('appointment', 'cancelled'),
  noShow: statusChipLabel('appointment', 'no_show'),
};

/** Display order for the 8 controlled not-converted reason tokens. */
export const NOT_CONVERTED_REASON_ORDER: readonly OwnerLeakageNotConvertedReason[] =
  [
    'price',
    'location',
    'timing',
    'no_response',
    'chose_competitor',
    'not_interested',
    'duplicate',
    'other',
  ];

const REASON_LABELS: Record<OwnerLeakageNotConvertedReason, string> = {
  price: 'Price',
  location: 'Location',
  timing: 'Timing',
  no_response: 'No response',
  chose_competitor: 'Chose competitor',
  not_interested: 'Not interested',
  duplicate: 'Duplicate',
  other: 'Other',
};

export interface FunnelBucketRow {
  key: FunnelBucketKey;
  label: string;
  count: number;
}

export interface FunnelReasonRow {
  reason: OwnerLeakageNotConvertedReason;
  label: string;
  count: number;
}

export interface FunnelClinicRow {
  clinicId: number;
  label: string;
  counts: OwnerLeakageFunnelCounts;
}

export interface FunnelSourceRow {
  source: string;
  label: string;
  counts: OwnerLeakageFunnelCounts;
}

export interface FunnelStageRateRow {
  key: 'noShowRate' | 'fulfilledRate' | 'notConvertedRate';
  label: string;
  /** Formatted percent string, or 'n/a' when the rate is null (booked = 0). */
  value: string;
}

export interface LeakageFunnelView {
  /** Cohort denominator: appointments booked in-window. */
  booked: number;
  /** The 5 buckets in display order. */
  bucketRows: FunnelBucketRow[];
  /** All 8 reason tokens in display order (0-filled). */
  reasonRows: FunnelReasonRow[];
  /** sum(byReason); may exceed notConverted when an appointment cites several. */
  reasonTotal: number;
  perClinicRows: FunnelClinicRow[];
  perSourceRows: FunnelSourceRow[];
  stageRateRows: FunnelStageRateRow[];
}

/** Percent in [0,1] -> "x.x%"; null -> 'n/a' (never a fabricated 0%). */
export function formatFunnelRate(rate: number | null): string {
  if (rate === null) return 'n/a';
  return `${Math.round(rate * 1000) / 10}%`;
}

/** Humanize a source token for display (e.g. 'paid_search' -> 'Paid search'). */
export function formatSourceLabel(source: string): string {
  if (source === 'unknown') return 'Unknown source';
  const spaced = source.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Build the display-ready view-model for the appointment funnel block. */
export function buildLeakageFunnelView(
  funnel: OwnerLeakageAppointmentFunnel,
): LeakageFunnelView {
  const bucketRows: FunnelBucketRow[] = FUNNEL_BUCKET_ORDER.map((key) => ({
    key,
    label: BUCKET_LABELS[key],
    count: funnel.totals[key],
  }));

  const reasonRows: FunnelReasonRow[] = NOT_CONVERTED_REASON_ORDER.map(
    (reason) => ({
      reason,
      label: REASON_LABELS[reason],
      count: funnel.byReason[reason] ?? 0,
    }),
  );
  const reasonTotal = reasonRows.reduce((sum, r) => sum + r.count, 0);

  const perClinicRows: FunnelClinicRow[] = funnel.perClinic.map(
    (c: OwnerLeakageFunnelPerClinic) => ({
      clinicId: c.clinicId,
      label: `Clinic ${c.clinicId}`,
      counts: toCounts(c),
    }),
  );

  const perSourceRows: FunnelSourceRow[] = funnel.perSource.map(
    (s: OwnerLeakageFunnelPerSource) => ({
      source: s.source,
      label: formatSourceLabel(s.source),
      counts: toCounts(s),
    }),
  );

  const stageRateRows: FunnelStageRateRow[] = [
    {
      key: 'noShowRate',
      label: 'No-show rate',
      value: formatFunnelRate(funnel.stageRates.noShowRate),
    },
    {
      key: 'fulfilledRate',
      label: 'Fulfilled rate',
      value: formatFunnelRate(funnel.stageRates.fulfilledRate),
    },
    {
      key: 'notConvertedRate',
      label: 'Not-converted rate',
      value: formatFunnelRate(funnel.stageRates.notConvertedRate),
    },
  ];

  return {
    booked: funnel.totals.booked,
    bucketRows,
    reasonRows,
    reasonTotal,
    perClinicRows,
    perSourceRows,
    stageRateRows,
  };
}

/** Strip the row-identity field down to the shared counts shape. */
function toCounts(
  row: OwnerLeakageFunnelCounts,
): OwnerLeakageFunnelCounts {
  return {
    booked: row.booked,
    cancelled: row.cancelled,
    noShow: row.noShow,
    fulfilled: row.fulfilled,
    notConverted: row.notConverted,
    active: row.active,
  };
}
